import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppStore } from '@/stores/app.store'
import {
  Assignment,
  AssignmentSubmission,
  AssignmentFilter,
  AssignmentListResponse,
  AssignmentStats,
  CreateAssignmentPayload,
  GradeAssignmentPayload,
  SubmitAssignmentPayload,
  UpdateAssignmentPayload,
  SubmissionFilter,
  SubmissionListResponse,
} from '@/types/assignments'
import { assignmentService } from '@/services/assignmentService'

// Query keys for consistent caching
export const assignmentKeys = {
  all: ['assignments'] as const,
  lists: () => [...assignmentKeys.all, 'list'] as const,
  list: (filters: AssignmentFilter) => [...assignmentKeys.lists(), filters] as const,
  details: () => [...assignmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...assignmentKeys.details(), id] as const,
  stats: () => [...assignmentKeys.all, 'stats'] as const,
}

// Custom hook for assignments with caching
export function useAssignments(filters: AssignmentFilter = {}) {
  const accessToken = useAppStore((state) => state.accessToken)

  return useQuery<AssignmentListResponse>({
    queryKey: assignmentKeys.list(filters),
    queryFn: () => assignmentService.getAssignments(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    enabled: Boolean(accessToken),
  })
}

// Custom hook for assignment stats
export function useAssignmentStats() {
  return useQuery<AssignmentStats | null>({
    queryKey: assignmentKeys.stats(),
    queryFn: () => assignmentService.getAssignmentStats(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })
}

// Custom hook for single assignment
export function useAssignment(id: string) {
  return useQuery<Assignment | null>({
    queryKey: assignmentKeys.detail(id),
    queryFn: () => assignmentService.getAssignment(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

// Mutation for creating assignments
export function useCreateAssignment() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: CreateAssignmentPayload) => assignmentService.createAssignment(data),
    onSuccess: () => {
      // Invalidate and refetch assignments
      queryClient.invalidateQueries({ queryKey: assignmentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: assignmentKeys.stats() })
    },
  })
}

// Mutation for submitting assignments
export function useSubmitAssignment() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (payload: SubmitAssignmentPayload) =>
      assignmentService.submitAssignment(payload),
    onSuccess: (result, payload) => {
      if (payload.assignmentId) {
        queryClient.invalidateQueries({ queryKey: assignmentKeys.detail(payload.assignmentId) })
      }
      queryClient.invalidateQueries({ queryKey: assignmentKeys.stats() })
    },
  })
}

// Mutation for grading assignments
export function useGradeAssignment() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (payload: GradeAssignmentPayload) => assignmentService.gradeSubmission(payload),
    onSuccess: (_, variables) => {
      if (variables?.assignmentId) {
        queryClient.invalidateQueries({ queryKey: assignmentKeys.detail(variables.assignmentId) })
      }
      queryClient.invalidateQueries({ queryKey: assignmentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: assignmentKeys.stats() })
    },
  })
}

export function useUpdateAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: UpdateAssignmentPayload) => assignmentService.updateAssignment(payload),
    onSuccess: (result) => {
      if (result) {
        queryClient.invalidateQueries({ queryKey: assignmentKeys.detail(result.id) })
      }
      queryClient.invalidateQueries({ queryKey: assignmentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: assignmentKeys.stats() })
    },
  })
}

export function useDeleteAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (assignmentId: string) => assignmentService.deleteAssignment(assignmentId),
    onSuccess: (_, assignmentId) => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.lists() })
      if (assignmentId) {
        queryClient.invalidateQueries({ queryKey: assignmentKeys.detail(assignmentId) })
      }
      queryClient.invalidateQueries({ queryKey: assignmentKeys.stats() })
    },
  })
}

export function useAssignmentSubmissions(assignmentId: string, filters?: SubmissionFilter) {
  const normalizedFilters = useMemo(() => {
    if (!filters) return {}
    const entries = Object.entries(filters).filter(([, value]) => value !== undefined && value !== '')
    return Object.fromEntries(entries)
  }, [filters])

  return useQuery<SubmissionListResponse>({
    queryKey: [...assignmentKeys.detail(assignmentId), 'submissions', normalizedFilters],
    queryFn: () => assignmentService.getSubmissions(
      assignmentId,
      Object.keys(normalizedFilters).length ? normalizedFilters : undefined
    ),
    enabled: Boolean(assignmentId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useLearnerAssignmentSubmission(assignmentId: string | undefined) {
  return useQuery<AssignmentSubmission | null>({
    queryKey: assignmentId ? [...assignmentKeys.detail(assignmentId), 'my-submission'] : ['assignments', 'my-submission'],
    queryFn: () => (assignmentId ? assignmentService.getMySubmissionForAssignment(assignmentId) : Promise.resolve(null)),
    enabled: Boolean(assignmentId),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useAssignmentSubmission(assignmentId: string | undefined, submissionId: string | undefined) {
  return useQuery<AssignmentSubmission | null>({
    queryKey:
      assignmentId && submissionId
        ? [...assignmentKeys.detail(assignmentId), 'submission', submissionId]
        : ['assignments', 'submission'],
    queryFn: () => {
      if (!assignmentId || !submissionId) {
        throw new Error('Assignment ID and submission ID are required')
      }
      return assignmentService.getSubmission(assignmentId, submissionId)
    },
    enabled: Boolean(assignmentId && submissionId),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}
